import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Banner,
  Button,
  Card,
  DataTable,
  Dialog,
  Field,
  Input,
  PageHead,
  PersonCell,
  Pill,
  SelectX,
  SettingRow,
  Skeleton,
  Spinner,
  Tabs,
  Textarea,
  Toggle,
  useToast,
  type Column,
} from "@guestpilot/ui-kit";
import { useGet, useWrite } from "../api/hooks";
import type { PersonRef, Schema } from "../api/types";
import { PersonPicker } from "../components/PersonPicker";
import { fieldErrorsOf, useHandleApiError } from "../errors";
import { useI18n } from "../i18n";
import { useSession } from "../session";
import { authenticateInTeams } from "../teams";
import { PurgeDialog } from "./AccountState";

type Settings = Schema<"Settings">;
type Removal = Schema<"RemovalStatusInfo">;
type Reason = Schema<"AccessReason">;
type Role = Schema<"RoleAssignment">;
type Beta = Schema<"BetaFlag">;
type Activation = Schema<"ActivationInfo">;

const TABS = ["general", "removal", "reasons", "roles", "beta"] as const;
type Tab = (typeof TABS)[number];

/** Org-wide defaults, removal, reasons, roles and beta features. One Save for the form; the removal mode has its own consent flow (BR-130, BR-163). */
export function Settings() {
  const { t } = useI18n();
  const s = useSession();
  const [tab, setTab] = useState<Tab>("general");
  const visible = TABS.filter((k) =>
    k === "roles" ? s.can("ROLE_MANAGE") : k === "beta" ? s.can("SETTINGS_READ") : true,
  );
  return (
    <div className="gp-page">
      <PageHead title={t("ui:settings.title")} subtitle={t("ui:settings.lead")} />
      <LegalNotice />
      <FeedNotice />
      <Tabs
        label={t("ui:settings.title")}
        value={tab}
        onChange={(k) => setTab(k as Tab)}
        tabs={visible.map((k) => ({ key: k, label: t(`ui:settings.tab.${k}`) }))}
      />
      {tab === "general" ? <General /> : null}
      {tab === "removal" ? <RemovalSection /> : null}
      {tab === "reasons" ? <Reasons /> : null}
      {tab === "roles" ? <Roles /> : null}
      {tab === "beta" ? <BetaSection /> : null}
      {s.isPrimaryAdmin ? <DangerZone /> : null}
    </div>
  );
}

/** Updated legal documents are announced 30 days ahead; an Admin may accept explicitly, nobody is locked out (BR-180). */
function LegalNotice() {
  const { t } = useI18n();
  const s = useSession();
  const fail = useHandleApiError();
  const accept = useWrite<unknown, Schema<"LegalAcceptance">>("POST", "/api/v1/tenant/legal-acceptance", [
    "/api/v1/me",
  ]);
  const u = s.me.legalUpdate;
  if (!u || u.accepted) return null;
  const v = u.versions ?? {};
  return (
    <Banner
      tone="info"
      action={
        <Button
          disabledReason={s.gate("SETTINGS_EDIT")}
          onClick={() =>
            accept.mutate(
              {
                body: {
                  tosVersion: v["tos"] ?? "",
                  privacyVersion: v["privacy"] ?? "",
                  dpaVersion: v["dpa"] ?? "",
                  retentionVersion: v["retention"] ?? "",
                  ...(v["portability"] ? { portabilityVersion: v["portability"] } : {}),
                },
              },
              { onError: fail },
            )
          }
        >
          {t("ui:settings.legalAccept")}
        </Button>
      }
    >
      {t("ui:settings.legalUpdate", {
        date: u.effectiveAt ? new Date(u.effectiveAt).toLocaleDateString(s.me.locale) : "",
      })}
    </Banner>
  );
}

/** The Teams activity feed needs one more application permission (D-148): until an Admin consents, notices go to the inbox and by email. */
function FeedNotice() {
  const { t } = useI18n();
  const s = useSession();
  const q = useGet<{ activityFeed: "granted" | "missing" | "unknown"; consentUrl: string | null }>(
    "/api/v1/settings/notification-channels",
    undefined,
    { enabled: s.can("SETTINGS_READ") },
  );
  const [busy, setBusy] = useState(false);
  if (q.data?.activityFeed !== "missing") return null;
  const url = q.data.consentUrl;
  return (
    <Banner
      tone="warn"
      action={
        url ? (
          <Button
            disabled={busy}
            onClick={() => {
              setBusy(true);
              authenticateInTeams(url)
                .then(() => q.refetch())
                .catch(() => undefined /* closed without consenting */)
                .finally(() => setBusy(false));
            }}
          >
            {t("ui:settings.feedConsent")}
          </Button>
        ) : undefined
      }
    >
      {t("ui:settings.feedMissing")}
    </Banner>
  );
}

const num = (v: string): number => (v === "" ? 0 : Number(v));

function General() {
  const { t } = useI18n();
  const s = useSession();
  const toast = useToast();
  const fail = useHandleApiError();
  const q = useGet<Settings>("/api/v1/settings");
  const save = useWrite<Settings, Settings>("PUT", "/api/v1/settings", [
    "/api/v1/settings",
    "/api/v1/me",
    "/api/v1/dashboard",
    "/api/v1/exceptions",
    "/api/v1/participants",
  ]);
  const [draft, setDraft] = useState<Settings | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  useEffect(() => {
    if (q.data && !draft) setDraft(q.data);
  }, [q.data, draft]);
  if (!draft) return <Skeleton rows={6} />;
  const dirty = JSON.stringify(draft) !== JSON.stringify(q.data);
  const set = <K extends keyof Settings>(k: K, v: Settings[K]) => setDraft({ ...draft, [k]: v });
  const gate = s.gate("SETTINGS_EDIT");
  const field = (k: keyof Settings) => errors[k as string];
  const onSave = () =>
    save.mutate(
      { body: draft },
      {
        onSuccess: (r) => {
          setDraft(r);
          setErrors({});
          toast.success(t("ui:settings.saved"));
        },
        onError: (e) => {
          setErrors(fieldErrorsOf(e));
          fail(e);
        },
      },
    );
  return (
    <>
      <div className="gp-actions" style={{ justifyContent: "space-between" }}>
        <span className="t-body-sm gp-muted" role="status">
          {dirty ? t("ui:settings.unsaved") : ""}
        </span>
        <Button
          variant="primary"
          disabledReason={gate ?? (dirty ? undefined : t("ui:settings.nothingToSave"))}
          onClick={onSave}
        >
          {t("common:save")}
        </Button>
      </div>
      <Card title={t("ui:settings.review")}>
        <SettingRow label={t("ui:settings.frequency")} description={t("ui:settings.frequencyHelp")}>
          <SelectX
            aria-label={t("ui:settings.frequency")}
            disabled={Boolean(gate)}
            value={draft.reviewFrequency ?? "quarterly"}
            options={(["monthly", "quarterly", "semiannual", "annual"] as const).map((v) => ({
              value: v,
              label: t(`ui:settings.freq.${v}`),
            }))}
            onChange={(e) => set("reviewFrequency", e.target.value as Settings["reviewFrequency"])}
          />
        </SettingRow>
        <SettingRow label={t("ui:settings.mode")} description={t("ui:settings.modeHelp")}>
          <SelectX
            aria-label={t("ui:settings.mode")}
            disabled={Boolean(gate)}
            value={draft.reviewFrequencyMode ?? "remind_admin"}
            options={(["remind_admin", "auto_launch"] as const).map((v) => ({
              value: v,
              label: t(`ui:settings.modes.${v}`),
            }))}
            onChange={(e) => set("reviewFrequencyMode", e.target.value as Settings["reviewFrequencyMode"])}
          />
        </SettingRow>
        <SettingRow label={t("ui:settings.scan")}>
          <SelectX
            aria-label={t("ui:settings.scan")}
            disabled={Boolean(gate)}
            value={draft.scanCadence ?? "daily"}
            options={(["daily", "weekly"] as const).map((v) => ({
              value: v,
              label: t(`ui:settings.cadence.${v}`),
            }))}
            onChange={(e) => set("scanCadence", e.target.value as Settings["scanCadence"])}
          />
        </SettingRow>
        <Field label={t("ui:settings.expiryWarning")} error={field("expiryWarningDays")}>
          <Input
            type="number"
            min={0}
            disabled={Boolean(gate)}
            value={draft.expiryWarningDays ?? 0}
            onChange={(e) => set("expiryWarningDays", num(e.target.value))}
          />
        </Field>
        <Field label={t("ui:settings.window")} error={field("defaultCampaignWindowDays")}>
          <Input
            type="number"
            min={1}
            disabled={Boolean(gate)}
            value={draft.defaultCampaignWindowDays ?? 14}
            onChange={(e) => set("defaultCampaignWindowDays", num(e.target.value))}
          />
        </Field>
      </Card>
      <Card title={t("ui:settings.reminders")}>
        <Field label={t("ui:settings.before")} error={field("reminderBeforeDays")}>
          <Input
            type="number"
            min={0}
            disabled={Boolean(gate)}
            value={draft.reminderBeforeDays ?? 3}
            onChange={(e) => set("reminderBeforeDays", num(e.target.value))}
          />
        </Field>
        <Field label={t("ui:settings.repeat")} error={field("reminderRepeatDays")}>
          <Input
            type="number"
            min={1}
            disabled={Boolean(gate)}
            value={draft.reminderRepeatDays ?? 2}
            onChange={(e) => set("reminderRepeatDays", num(e.target.value))}
          />
        </Field>
        <Toggle
          label={t("ui:settings.summary")}
          checked={draft.completionSummaryToAdmin ?? false}
          disabled={Boolean(gate)}
          onChange={(v) => set("completionSummaryToAdmin", v)}
        />
        <Field label={t("ui:settings.sendHour")}>
          <Input
            type="number"
            min={0}
            max={23}
            disabled={Boolean(gate)}
            value={draft.reminderSendHour ?? 9}
            onChange={(e) => set("reminderSendHour", num(e.target.value))}
          />
        </Field>
        <Field label={t("ui:settings.timeZone")} error={field("timeZone")}>
          <Input
            disabled={Boolean(gate)}
            value={draft.timeZone ?? ""}
            onChange={(e) => set("timeZone", e.target.value)}
          />
        </Field>
      </Card>
      <Card title={t("ui:settings.escalation")}>
        <Field label={t("ui:settings.escalationEmail")} error={field("escalationContactEmail")}>
          <Input
            type="email"
            disabled={Boolean(gate)}
            value={draft.escalationContactEmail ?? ""}
            onChange={(e) => set("escalationContactEmail", e.target.value || null)}
          />
        </Field>
        <Field label={t("ui:settings.escalateAfter")} error={field("escalateAfterOverdueDays")}>
          <Input
            type="number"
            min={1}
            disabled={Boolean(gate)}
            value={draft.escalateAfterOverdueDays ?? 7}
            onChange={(e) => set("escalateAfterOverdueDays", num(e.target.value))}
          />
        </Field>
        <Toggle
          label={t("ui:settings.escalateUnknown")}
          checked={draft.escalateUnknownDomainImmediately ?? false}
          disabled={Boolean(gate)}
          onChange={(v) => set("escalateUnknownDomainImmediately", v)}
        />
      </Card>
      <Card title={t("ui:settings.exceptions")}>
        <Toggle
          label={t("ui:settings.exceptionAdmin")}
          checked={draft.exceptionRequiresAdminApproval ?? false}
          disabled={Boolean(gate)}
          onChange={(v) => set("exceptionRequiresAdminApproval", v)}
        />
        <Field label={t("ui:settings.maxException")} error={field("maxExceptionDays")}>
          <Input
            type="number"
            min={1}
            disabled={Boolean(gate)}
            value={draft.maxExceptionDays ?? 365}
            onChange={(e) => set("maxExceptionDays", num(e.target.value))}
          />
        </Field>
        <Field
          label={t("ui:settings.maxAccess")}
          help={t("ui:settings.maxAccessHelp")}
          error={field("maxAccessDays")}
        >
          <Input
            type="number"
            min={1}
            disabled={Boolean(gate)}
            value={draft.maxAccessDays ?? ""}
            onChange={(e) => set("maxAccessDays", e.target.value === "" ? null : num(e.target.value))}
          />
        </Field>
        <Field label={t("ui:settings.instructions")}>
          <Textarea
            disabled={Boolean(gate)}
            value={draft.defaultCampaignInstructions ?? ""}
            onChange={(e) => set("defaultCampaignInstructions", e.target.value)}
          />
        </Field>
      </Card>
    </>
  );
}

/** Removal mode (BR-091, BR-161…BR-165): choosing a mode that needs more permission returns a consent URL and the mode stays unchanged until verified. */
function RemovalSection() {
  const { t } = useI18n();
  const s = useSession();
  const fail = useHandleApiError();
  const nav = useNavigate();
  const q = useGet<Removal>("/api/v1/removal");
  const act = useGet<Activation>("/api/v1/activation", undefined, { enabled: s.can("TEAM_ACTIVATE") });
  const mode = useWrite<Removal, Schema<"RemovalModeChange">>("PUT", "/api/v1/removal/mode", [
    "/api/v1/removal",
    "/api/v1/me",
  ]);
  const check = useWrite<Removal>("POST", "/api/v1/removal/permission-check", [
    "/api/v1/removal",
    "/api/v1/me",
  ]);
  const deleg = useWrite<Removal, Schema<"DelegationChange">>("PUT", "/api/v1/removal/delegation", [
    "/api/v1/removal",
  ]);
  const oneClick = useWrite<Activation, Schema<"OneClickChange">>("PUT", "/api/v1/activation/one-click", [
    "/api/v1/activation",
  ]);
  const [confirmDeleg, setConfirmDeleg] = useState(false);
  const [busy, setBusy] = useState(false);
  const gate = s.gate("REMOVAL_MODE_MANAGE");
  if (!q.data) return <Skeleton rows={4} />;
  const r = q.data;
  const pending = r.consent === "pending" || Boolean(r.requestedMode);

  // the status is read again whatever the check answers: the consent callback of the API may already have completed the mode
  const recheck = () => check.mutate(undefined, { onError: fail, onSettled: () => void q.refetch() });
  const consent = async (url: string) => {
    setBusy(true);
    try {
      await authenticateInTeams(url);
      const fresh = await q.refetch();
      if (fresh.data && (fresh.data.consent === "pending" || fresh.data.requestedMode)) recheck();
    } catch {
      /* closed without consenting */
    } finally {
      setBusy(false);
    }
  };
  const choose = (m: Schema<"RemovalMode">) =>
    mode.mutate(
      { body: { mode: m } },
      {
        onSuccess: (res) => {
          if (res.consentUrl) void consent(res.consentUrl);
        },
        onError: fail,
      },
    );

  return (
    <>
      <Card title={t("ui:removalMode.title")}>
        <p className="t-body gp-muted" style={{ margin: 0 }}>
          {t("ui:removalMode.lead")}
        </p>
        <fieldset className="gp-stack" style={{ border: 0, padding: 0, margin: 0 }}>
          <legend className="gp-sr-only">{t("ui:removalMode.title")}</legend>
          {(["off", "team_membership", "entra_account"] as const).map((m) => (
            <label key={m} className="gp-check">
              <input
                type="radio"
                name="mode"
                checked={r.mode === m}
                disabled={Boolean(gate)}
                onChange={() => choose(m)}
              />
              <span className="gp-person-text">
                <span className="t-body-strong">{t(`ui:removalMode.${m}.label`)}</span>
                <span className="t-body-sm gp-muted">{t(`ui:removalMode.${m}.help`)}</span>
              </span>
            </label>
          ))}
        </fieldset>
        {pending ? (
          <div className="gp-row" role="status">
            <Spinner label={t("ui:removalMode.waiting")} />
            {r.consentUrl ? (
              <Button disabled={busy} onClick={() => void consent(r.consentUrl!)}>
                {t("ui:removalMode.grant")}
              </Button>
            ) : null}
            <Button variant="ghost" onClick={recheck}>
              {t("ui:removalMode.recheck")}
            </Button>
          </div>
        ) : null}
        {r.consent === "lost" ? <Banner tone="danger">{t("ui:removalMode.lost")}</Banner> : null}
      </Card>
      <Card title={t("ui:removalMode.delegation")}>
        <Toggle
          label={t("ui:removalMode.delegationLabel")}
          checked={Boolean(r.delegationEnabled)}
          // removal from a Team is always immediate (D-141): the switch and the delay only concern deleting the account
          disabled={Boolean(gate) || r.mode !== "entra_account"}
          onChange={(v) =>
            v ? setConfirmDeleg(true) : deleg.mutate({ body: { enabled: false } }, { onError: fail })
          }
        />
        {r.mode === "team_membership" ? (
          <p className="t-body-sm gp-muted" style={{ margin: 0 }}>
            {t("ui:removalMode.immediateNote")}
          </p>
        ) : null}
        {r.mode === "entra_account" ? (
          <p className="t-body-sm gp-muted" style={{ margin: 0 }}>
            {t("ui:removalMode.delay", { h: r.executionDelayHours ?? 0 })}
          </p>
        ) : null}
      </Card>
      {s.can("TEAM_ACTIVATE") ? (
        <Card title={t("ui:oneClick.title")}>
          <Toggle
            label={t("ui:oneClick.label")}
            checked={Boolean(act.data?.oneClick?.enabled)}
            pending={act.data?.oneClick?.consent === "pending"}
            pendingLabel={t("ui:removalMode.waiting")}
            disabled={Boolean(gate)}
            onChange={(v) =>
              oneClick.mutate(
                { body: { enabled: v } },
                {
                  onSuccess: (a) => {
                    if (a.oneClick?.consentUrl) void consent(a.oneClick.consentUrl);
                  },
                  onError: fail,
                },
              )
            }
          />
          <p className="t-body-sm gp-muted" style={{ margin: 0 }}>
            {t("ui:oneClick.help")}
          </p>
          <Button variant="ghost" onClick={() => nav("/teams?activate=1")}>
            {t("ui:teams.activate")}
          </Button>
        </Card>
      ) : null}
      <Dialog
        open={confirmDeleg}
        onClose={() => setConfirmDeleg(false)}
        title={t("ui:removalMode.delegationConfirmTitle")}
        description={t("ui:removalMode.delegationConfirmBody")}
        footer={
          <>
            <Button onClick={() => setConfirmDeleg(false)}>{t("common:cancel")}</Button>
            <Button
              variant="danger"
              onClick={() =>
                deleg.mutate(
                  { body: { enabled: true, confirmed: true } },
                  { onSuccess: () => setConfirmDeleg(false), onError: fail },
                )
              }
            >
              {t("common:confirm")}
            </Button>
          </>
        }
      />
    </>
  );
}

/** Access reasons: rename or retire; the built-in "other" cannot be retired (BR-058, BR-059). */
function Reasons() {
  const { t } = useI18n();
  const s = useSession();
  const fail = useHandleApiError();
  const q = useGet<Reason[]>("/api/v1/access-reasons");
  const create = useWrite<Reason, Schema<"AccessReasonWrite">>("POST", "/api/v1/access-reasons", [
    "/api/v1/access-reasons",
  ]);
  const update = useWrite<Reason, Schema<"AccessReasonWrite">>("PATCH", "/api/v1/access-reasons/:id", [
    "/api/v1/access-reasons",
  ]);
  const [label, setLabel] = useState("");
  const gate = s.gate("REASON_MANAGE");
  const columns: Column<Reason>[] = [
    {
      key: "label",
      header: t("ui:reasons.label"),
      render: (r) => <span className="t-body-strong">{r.label}</span>,
    },
    {
      key: "built",
      header: t("ui:table.actions"),
      render: (r) => (r.builtIn ? <Pill>{t("ui:reasons.builtIn")}</Pill> : null),
    },
    {
      key: "state",
      header: t("ui:users.col.status"),
      render: (r) =>
        r.retired ? (
          <Pill variant="neutral">{t("ui:reasons.retired")}</Pill>
        ) : (
          <Pill variant="success">{t("ui:reasons.inUse")}</Pill>
        ),
    },
    {
      key: "act",
      header: t("ui:table.actions"),
      render: (r) => (
        <Button
          variant="ghost"
          disabledReason={
            gate ?? (r.key === "other" && !r.retired ? t("ui:reasons.otherPermanent") : undefined)
          }
          onClick={() =>
            update.mutate(
              { params: { id: r.id ?? "" }, body: { label: r.label ?? "", retired: !r.retired } },
              { onError: fail },
            )
          }
        >
          {r.retired ? t("ui:reasons.restore") : t("ui:reasons.retire")}
        </Button>
      ),
    },
  ];
  return (
    <Card title={t("ui:settings.tab.reasons")}>
      <DataTable
        caption={t("ui:settings.tab.reasons")}
        columns={columns}
        rows={q.data}
        loading={q.isLoading}
        error={q.isError}
        onRetry={() => void q.refetch()}
        errorTitle={t("ui:table.error")}
        retryLabel={t("common:retry")}
        rowKey={(r) => r.id ?? ""}
        emptyTitle="-"
      />
      <div className="gp-row">
        <Input
          aria-label={t("ui:reasons.new")}
          placeholder={t("ui:reasons.new")}
          value={label}
          onChange={(e) => setLabel(e.target.value)}
        />
        <Button
          variant="primary"
          disabledReason={gate ?? (label.trim() ? undefined : t("ui:reasons.new"))}
          onClick={() =>
            create.mutate({ body: { label: label.trim() } }, { onSuccess: () => setLabel(""), onError: fail })
          }
        >
          {t("ui:reasons.add")}
        </Button>
      </div>
    </Card>
  );
}

/** Roles for internal users only; the last Admin cannot be removed; only the primary Admin transfers the primary role (BR-008, BR-009, BR-228). */
function Roles() {
  const { t } = useI18n();
  const s = useSession();
  const fail = useHandleApiError();
  const q = useGet<Role[]>("/api/v1/roles");
  const invalidate = ["/api/v1/roles", "/api/v1/me"];
  const grant = useWrite<Role, Schema<"RoleGrant">>("POST", "/api/v1/roles", invalidate);
  const revoke = useWrite("DELETE", "/api/v1/roles/:personId", invalidate);
  const primary = useWrite("POST", "/api/v1/roles/:personId/make-primary", invalidate);
  const [person, setPerson] = useState<PersonRef | null>(null);
  const [role, setRole] = useState<"admin" | "viewer">("viewer");
  const gate = s.gate("ROLE_MANAGE");
  const columns: Column<Role>[] = [
    {
      key: "p",
      header: t("ui:users.col.person"),
      render: (r) => <PersonCell name={r.person?.displayName ?? ""} sub={r.person?.email} />,
    },
    {
      key: "r",
      header: t("ui:roles.role"),
      render: (r) =>
        r.isPrimary ? (
          <Pill variant="brand">{t("ui:roles.primary")}</Pill>
        ) : (
          <Pill>{t(`ui:roles.${r.role}`)}</Pill>
        ),
    },
    {
      key: "a",
      header: t("ui:table.actions"),
      render: (r) => (
        <div className="gp-row">
          {r.role === "admin" && !r.isPrimary ? (
            <Button
              variant="ghost"
              disabledReason={s.isPrimaryAdmin ? undefined : t("ui:state.purgePrimaryOnly")}
              onClick={() => primary.mutate({ params: { personId: r.person?.id ?? "" } }, { onError: fail })}
            >
              {t("ui:roles.makePrimary")}
            </Button>
          ) : null}
          {!r.isPrimary ? (
            <Button
              variant="ghost"
              disabledReason={gate}
              onClick={() => revoke.mutate({ params: { personId: r.person?.id ?? "" } }, { onError: fail })}
            >
              {t("ui:roles.revoke")}
            </Button>
          ) : null}
        </div>
      ),
    },
  ];
  return (
    <Card title={t("ui:settings.tab.roles")}>
      <p className="t-body gp-muted" style={{ margin: 0 }}>
        {t("ui:roles.lead")}
      </p>
      <DataTable
        caption={t("ui:settings.tab.roles")}
        columns={columns}
        rows={q.data}
        loading={q.isLoading}
        error={q.isError}
        onRetry={() => void q.refetch()}
        errorTitle={t("ui:table.error")}
        retryLabel={t("common:retry")}
        rowKey={(r) => r.person?.id ?? ""}
        emptyTitle="-"
      />
      <div className="gp-row" style={{ alignItems: "flex-end" }}>
        <div style={{ flex: 1, minWidth: "calc(var(--space-64) * 3)" }}>
          <Field label={t("ui:roles.add")}>
            <PersonPicker label={t("ui:roles.add")} value={person} onChange={setPerson} />
          </Field>
        </div>
        <SelectX
          aria-label={t("ui:roles.role")}
          value={role}
          options={[
            { value: "admin", label: t("ui:roles.admin") },
            { value: "viewer", label: t("ui:roles.viewer") },
          ]}
          onChange={(e) => setRole(e.target.value as "admin" | "viewer")}
        />
        <Button
          variant="primary"
          disabledReason={gate ?? (person ? undefined : t("ui:delegate.pick"))}
          onClick={() =>
            grant.mutate(
              { body: { personId: person!.id, role } },
              { onSuccess: () => setPerson(null), onError: fail },
            )
          }
        >
          {t("ui:roles.grant")}
        </Button>
      </div>
    </Card>
  );
}

function BetaSection() {
  const { t } = useI18n();
  const s = useSession();
  const fail = useHandleApiError();
  const q = useGet<Beta[]>("/api/v1/settings/beta");
  const set = useWrite<Beta, { enabled: boolean }>("PUT", "/api/v1/settings/beta/:key", [
    "/api/v1/settings/beta",
    "/api/v1/me",
  ]);
  const gate = s.gate("BETA_MANAGE");
  return (
    <Card title={t("ui:settings.tab.beta")}>
      <p className="t-body gp-muted" style={{ margin: 0 }}>
        {t("ui:beta.lead")}
      </p>
      {(q.data ?? []).length === 0 && !q.isLoading ? (
        <p className="t-body gp-muted" style={{ margin: 0 }}>
          {t("ui:beta.none")}
        </p>
      ) : null}
      {(q.data ?? []).map((f) => (
        <SettingRow key={f.key} label={f.key ?? ""} description={f.description}>
          <Toggle
            label={f.key ?? ""}
            checked={Boolean(f.enabled)}
            disabled={Boolean(gate)}
            onChange={(v) =>
              set.mutate({ params: { key: f.key ?? "" }, body: { enabled: v } }, { onError: fail })
            }
          />
        </SettingRow>
      ))}
    </Card>
  );
}

/** Danger zone: only the primary Admin asks for the deletion of all data (BR-175, BR-228). */
function DangerZone() {
  const { t } = useI18n();
  const s = useSession();
  const [open, setOpen] = useState(false);
  return (
    <Card title={t("ui:settings.danger")} tone="danger">
      <p className="t-body" style={{ margin: 0 }}>
        {t("ui:settings.dangerBody")}
      </p>
      <div className="gp-actions">
        <Button
          variant="danger"
          disabledReason={s.readOnly ? undefined : undefined}
          onClick={() => setOpen(true)}
        >
          {t("ui:state.requestPurge")}
        </Button>
      </div>
      <PurgeDialog open={open} onClose={() => setOpen(false)} tenantName={s.me.tenant.name} />
    </Card>
  );
}
